import { QueryInput } from "@/components/QueryInput";
import { AssistantTurn } from "@/components/Answer";
import { streamAssistant } from "@/lib/api";
import type { ParsedAssistant } from "@/lib/types";
import type { LayoutContext } from "@/components/Layout";
import {
  Beaker,
  Brain,
  Code2,
  Coins,
  Compass,
  Globe2,
  Hammer,
  Leaf,
  MapPin,
  Music,
  Newspaper,
  Rocket,
  Sparkles,
  Telescope,
  Utensils,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";

const ALL_SUGGESTIONS = [
  { icon: Newspaper, label: "Top news in Nepal today" },
  { icon: Beaker, label: "Explain CRISPR gene editing" },
  { icon: Code2, label: "What's new in React 19?" },
  { icon: Compass, label: "Plan a 5-day trip to Kyoto" },
  { icon: Telescope, label: "Latest discoveries from the JWST" },
  { icon: Brain, label: "How does the human memory work?" },
  { icon: Coins, label: "Why are interest rates moving?" },
  { icon: Rocket, label: "SpaceX upcoming launches this month" },
  { icon: Globe2, label: "Climate tipping points to watch in 2026" },
  { icon: Music, label: "Best new albums released this week" },
  { icon: Utensils, label: "Underrated street foods in Bangkok" },
  { icon: MapPin, label: "Hidden hikes near Pokhara" },
  { icon: Hammer, label: "Compare Bun vs Node for production APIs" },
  { icon: Leaf, label: "Daily habits backed by recent research" },
  { icon: Sparkles, label: "What is mechanistic interpretability?" },
];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function Home() {
  const navigate = useNavigate();
  const { reloadConversations } = useOutletContext<LayoutContext>();
  const [input, setInput] = useState("");
  const [active, setActive] = useState<{
    query: string;
    parsed: ParsedAssistant;
  } | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [seed, setSeed] = useState(0);

  const suggestions = useMemo(
    () => shuffle(ALL_SUGGESTIONS).slice(0, 4),
    [seed],
  );

  useEffect(() => {
    if (!streaming) return;
    const main = document.querySelector("main");
    main?.scrollTo({ top: main.scrollHeight, behavior: "smooth" });
  }, [active?.parsed.answer, streaming]);

  const submit = async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed || streaming) return;
    setInput("");
    setActive({
      query: trimmed,
      parsed: { answer: "", followUps: [], sources: [], done: false },
    });
    setStreaming(true);

    try {
      const { parsed } = await streamAssistant({
        endpoint: "/query",
        body: { query: trimmed },
        onUpdate: (p) => {
          setActive((cur) => (cur ? { query: cur.query, parsed: p } : cur));
        },
      });
      reloadConversations();
      if (parsed.conversationId) {
        navigate(`/c/${parsed.conversationId}`, { replace: true });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setStreaming(false);
    }
  };

  if (active) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 pt-6 pb-32 sm:px-6 sm:pt-10">
        <AssistantTurn
          query={active.query}
          parsed={active.parsed}
          streaming={streaming}
          onPickFollowUp={() => {}}
        />
      </div>
    );
  }

  return (
    <div className="relative mx-auto flex min-h-full w-full max-w-3xl flex-col items-center justify-center px-4 py-10 sm:px-6 sm:py-16">
      <div className="fade-up mb-8 flex flex-col items-center gap-3 text-center sm:mb-10">
        <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 ring-1 ring-primary/30 sm:size-14">
          <Compass className="size-6 text-primary sm:size-7" strokeWidth={2} />
        </div>
        <h1 className="font-serif text-4xl tracking-tight sm:text-5xl md:text-6xl">
          <span className="italic">where</span> knowledge begins
        </h1>
        <p className="text-xs text-muted-foreground sm:text-sm">
          Ask. Discover. Cite the web in one breath.
        </p>
      </div>

      <div className="fade-up w-full" style={{ animationDelay: "80ms" }}>
        <QueryInput
          value={input}
          onChange={setInput}
          onSubmit={() => submit(input)}
          placeholder="Ask anything, or paste a link..."
        />
      </div>

      <div
        className="fade-up mt-5 grid w-full grid-cols-1 gap-2 sm:mt-6 sm:grid-cols-2"
        style={{ animationDelay: "160ms" }}
      >
        {suggestions.map((s, i) => {
          const Icon = s.icon;
          return (
            <button
              key={s.label}
              onClick={() => submit(s.label)}
              className="group flex items-center gap-3 rounded-xl border border-border/60 bg-card/40 px-4 py-3 text-left text-sm text-muted-foreground transition-all hover:border-primary/40 hover:bg-card hover:text-foreground"
              style={{ animationDelay: `${200 + i * 40}ms` }}
            >
              <Icon className="size-4 shrink-0 text-primary/80 transition-transform group-hover:scale-110" />
              <span className="truncate">{s.label}</span>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => setSeed((n) => n + 1)}
        className="fade-up mt-4 text-[11px] tracking-wide text-muted-foreground/50 transition-colors hover:text-primary"
        style={{ animationDelay: "240ms" }}
      >
        ↻ shuffle suggestions
      </button>

      <div className="pointer-events-none absolute inset-x-0 bottom-3 hidden justify-center text-[11px] tracking-wide text-muted-foreground/50 sm:bottom-6 sm:flex">
        sanplexity · grounded answers, cited sources
      </div>
    </div>
  );
}

export default Home;
